<template>
  <v-container fluid class="relative header-space container">
    <div>
      <v-row>
        <v-col sm="12">
          <h1>Elasticsearch</h1>
          <p>Eine Übersicht zum Status von Elasticsearch.</p>
        </v-col>
        <v-col class="text-right">
          <v-btn
            class="ml-3 mb-3 primary"
            v-on:click="updateESMapping"
            :loading="updatingMapping"
            ><v-icon class="mr-1">mdi-refresh</v-icon>Update ElasticSearch
            Mapping</v-btn
          >
          <p>
            <small
              >Aktualisiere das ElasticSearch Mapping entsprechend der
              Definition im ReleaseService.</small
            ><br />
            <span>{{ esMappingUpdateResponse }}</span>
          </p>
          <p>
            <v-text-field
              v-model="indexToDelete"
              label="Index zum löschen"
            ></v-text-field>
            <small><b>Achtung:</b> Nur wenn du weißt was du da tust!</small
            ><br />
            <v-btn
              class="red white--text mt-3 mb-3"
              v-on:click="deleteIndex"
              :loading="deletingIndex"
              >Löschen</v-btn
            ><br />
            <span>{{ indexDeletionResponse }}</span>
          </p>
        </v-col>
      </v-row>
      <v-row>
        <v-col sm="12">
          <div v-show="initiated">
            <v-card class="mb-6">
              <v-simple-table v-if="!loading">
                <thead>
                  <tr>
                    <th class="text-left column-width" style="min-width: 250px">
                      Name
                    </th>
                    <th class="text-left column-width">Wert</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="metric in systemInformation"
                    v-bind:key="metric.name"
                  >
                    <td>{{ metric.name }}</td>
                    <td>{{ metric.value }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card>

            <v-card class="mb-6 pa-4">
              <v-row>
                <v-col cols="12" md="7">
                  <h3 class="mb-2">Blocked Contacts/Accounts (No Awards)</h3>
                  <p class="mb-0">
                    Alle Kontakte/Accounts, die im ES-Sync ohne Awards gefunden
                    und blockiert wurden.
                  </p>
                </v-col>
                <v-col cols="12" md="5" class="text-right">
                  <v-btn
                    class="mr-2"
                    outlined
                    v-on:click="getBlockedList"
                    :loading="loadingBlocked"
                  >
                    Reload
                  </v-btn>
                  <v-btn
                    color="red"
                    class="white--text"
                    v-on:click="confirmClearDialog = true"
                    :disabled="blockedEntities.length === 0"
                    :loading="clearingAll"
                  >
                    Unblock All
                  </v-btn>
                </v-col>
              </v-row>

              <v-simple-table v-if="!loadingBlocked">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Typ</th>
                    <th>Name</th>
                    <th>blocked_at</th>
                    <th>short_code</th>
                    <th>Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="blockedEntities.length === 0">
                    <td colspan="6">Keine betroffenen Entitäten gefunden.</td>
                  </tr>
                  <tr
                    v-for="entity in blockedEntities"
                    :key="entity.type + '-' + entity.id"
                  >
                    <td class="mono small grey--text text--darken-2">
                      {{ entity.id }}
                    </td>
                    <td>{{ entity.type }}</td>
                    <td>{{ entity.name }}</td>
                    <td>{{ formatDate(entity.blockedAt) }}</td>
                    <td>{{ entity.shortCode || "-" }}</td>
                    <td>
                      <v-btn
                        outlined
                        small
                        v-on:click="clearEntitySyncRequest(entity)"
                      >
                        Unblock
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <div v-else class="py-4 text-center grey--text">
                <v-progress-circular
                  color="grey"
                  indeterminate
                ></v-progress-circular>
              </div>

              <p v-if="clearAllResponse" class="mt-3 mb-0">
                {{ clearAllResponse }}
              </p>
            </v-card>

            <v-dialog v-model="confirmClearDialog" max-width="420">
              <v-card>
                <v-card-title class="headline"
                  >Unblock all listed entities?</v-card-title
                >
                <v-card-text>
                  Dadurch wird die no-awards Blockierung fuer alle gelisteten
                  Entitaeten entfernt.
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text v-on:click="confirmClearDialog = false"
                    >Abbrechen</v-btn
                  >
                  <v-btn
                    color="red"
                    class="white--text"
                    :loading="clearingAll"
                    v-on:click="clearAllBlocked"
                  >
                    Unblock All
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </div>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
export default {
  components: {
    //
  },
  data: () => ({
    initiated: false,
    esMappingUpdateResponse: null,
    indexDeletionResponse: null,
    systemInformation: [],
    blockedEntities: [],
    indexToDelete: null,
    loading: false,
    loadingBlocked: false,
    deletingIndex: false,
    updatingMapping: false,
    clearingAll: false,
    confirmClearDialog: false,
    clearAllResponse: null
  }),
  methods: {
    getList: async function () {
      const response = await this.$http.get('/elasticsearch/status')
      this.systemInformation = response.data
    },
    getBlockedList: async function () {
      this.loadingBlocked = true
      try {
        const response = await this.$http.get('/sync-blocked/list')
        this.blockedEntities = response.data
      } finally {
        this.loadingBlocked = false
      }
    },
    updateESMapping: async function () {
      this.updatingMapping = true
      const response = await this.$http.post('/elasticsearch/apply-mapping')
      this.esMappingUpdateResponse = response.data
      await this.getList()
      this.updatingMapping = false
    },
    deleteIndex: async function () {
      this.deletingIndex = true
      const response = await this.$http.delete(
        '/elasticsearch/delete/' + this.indexToDelete
      )
      this.indexDeletionResponse = response.data
      this.indexToDelete = null
      await this.getList()
      this.deletingIndex = false
    },
    clearEntitySyncRequest: async function (entity) {
      await this.$http.put(
        '/sync-blocked/' +
          entity.type +
          '/' +
          entity.id +
          '/clear-sync-request'
      )
      this.blockedEntities = this.blockedEntities.filter(
        (item) => !(item.id === entity.id && item.type === entity.type)
      )
    },
    clearAllBlocked: async function () {
      this.clearingAll = true
      try {
        const response = await this.$http.delete('/sync-blocked/clear-all')
        this.clearAllResponse = 'Unblocked entities: ' + response.data.cleared
        this.blockedEntities = []
        this.confirmClearDialog = false
      } finally {
        this.clearingAll = false
      }
    },
    formatDate: function (value) {
      if (!value) {
        return '-'
      }
      return new Date(value).toLocaleString()
    }
  },
  async created () {
    await this.$auth.waitForAuth()
    if (!this.$auth.isAuthenticated) {
      await this.$router.push('/')
      return
    }

    await Promise.allSettled([this.getList(), this.getBlockedList()])
    this.initiated = true
  }
}
</script>
